'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '@/shared/ui/shadcn/drawer';
import { Button } from '@/shared/ui/shadcn/button';
import { Star, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { toast } from '@/shared/lib/toast';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { useRenderPicker, useDownloadRender } from '../hooks/use-render-picker';
import { RenderGridItem } from './RenderGridItem';
import type { RenderPickerItem } from '../model/types';

interface RenderPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRenderSelected: (file: File) => void;
  /** Classes for the modal layer itself (content / overlay). Lets a caller that
   *  lives above the default z-50 stack (the fullscreen sketch editor) raise the
   *  picker with it — otherwise the picker opens *under* that layer and the
   *  click looks like it did nothing. */
  contentClassName?: string;
  overlayClassName?: string;
}

/**
 * Modal for selecting existing renders as image source.
 * Mobile: bottom sheet (Drawer). Desktop: centered Dialog.
 * Matches Apple RenderPickerView design.
 */
export function RenderPickerModal({
  isOpen,
  onClose,
  onRenderSelected,
  contentClassName,
  overlayClassName,
}: RenderPickerModalProps) {
  const t = useTranslations('renderPicker');
  const tCommon = useTranslations('common');
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useRenderPicker(favoritesOnly);

  const downloadMutation = useDownloadRender();

  // Flatten pages into single array
  const renders = data?.pages.flatMap((page) => page.items) ?? [];

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleRenderClick = useCallback(
    async (item: RenderPickerItem) => {
      if (downloadingId) return;

      setDownloadingId(item.id);

      try {
        const file = await downloadMutation.mutateAsync({
          imageUrl: item.imageUrl,
          filename: `render_${item.id}.jpg`,
        });

        onRenderSelected(file);
        onClose();
      } catch (err) {
        console.error('Failed to download render:', err);
        toast.error(t('downloadError'));
      } finally {
        setDownloadingId(null);
      }
    },
    [downloadingId, downloadMutation, onRenderSelected, onClose, t]
  );

  const handleToggleFavorites = useCallback(() => {
    setFavoritesOnly((prev) => !prev);
  }, []);

  // Header bar — matches Apple toolbar: cancel (left), title (center), star (right)
  const headerBar = (
    <div className="flex items-center justify-between gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="text-primary font-normal"
      >
        {tCommon('cancel')}
      </Button>
      <span className="text-sheet-title flex-1 text-center truncate">
        {t('title')}
      </span>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggleFavorites}
        aria-label={t('favoritesOnly')}
        className="h-9 w-9"
      >
        <Star
          className={cn(
            'w-5 h-5',
            favoritesOnly
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-foreground'
          )}
        />
      </Button>
    </div>
  );

  // Scrollable body content
  const bodyContent = (
    <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain">
      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{tCommon('loading')}</p>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <AlertCircle className="w-12 h-12 text-destructive" />
          <p className="text-destructive">{t('errorLoad')}</p>
          <Button
            variant="outline"
            onClick={() => refetch()}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t('retry')}
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && renders.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Star className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground text-center">
            {favoritesOnly ? t('emptyFavorites') : t('empty')}
          </p>
          {favoritesOnly && (
            <Button
              variant="outline"
              onClick={() => setFavoritesOnly(false)}
            >
              {t('showAll')}
            </Button>
          )}
        </div>
      )}

      {/* Renders grid — 2 columns like Apple */}
      {!isLoading && !error && renders.length > 0 && (
        <div className="grid grid-cols-2 gap-2 p-4">
          {renders.map((item) => (
            <RenderGridItem
              key={item.id}
              item={item}
              isDownloading={downloadingId === item.id}
              onClick={() => handleRenderClick(item)}
            />
          ))}
        </div>
      )}

      {/* Load more trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {isFetchingNextPage && (
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          )}
        </div>
      )}
    </div>
  );

  // Mobile: Drawer (bottom sheet)
  if (!isDesktop) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent
          className={cn('max-h-[85vh] flex flex-col', contentClassName)}
          overlayClassName={overlayClassName}
        >
          <DrawerTitle className="sr-only">{t('title')}</DrawerTitle>
          <div className="px-3 pt-1 pb-2">{headerBar}</div>
          {bodyContent}
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Dialog
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          'w-[95vw] max-w-2xl max-h-[80vh] flex flex-col',
          contentClassName
        )}
        overlayClassName={overlayClassName}
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle>{t('title')}</DialogTitle>
            <Button
              variant={favoritesOnly ? 'default' : 'outline'}
              size="sm"
              onClick={handleToggleFavorites}
              aria-label={t('favoritesOnly')}
              className="gap-2"
            >
              <Star
                className={cn(
                  'w-4 h-4',
                  favoritesOnly && 'fill-yellow-400 text-yellow-400'
                )}
              />
              <span className="hidden sm:inline">{t('favoritesOnly')}</span>
            </Button>
          </div>
        </DialogHeader>

        {bodyContent}

        <div className="flex-shrink-0 flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            {tCommon('cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
